import { ClickAwayListener } from "@mui/base";
import type { PopperPlacementType } from "@mui/base/Popper";
import { JSXElementConstructor, memo, MouseEvent, useMemo } from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Tooltip } from "../../tooltip/tooltip";
import {
  RedoButton,
  RedoButtonSize,
  RedoButtonTheme,
} from "../buttons/redo-button";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";

export interface RedoCommandMenuItem {
  Icon?: JSXElementConstructor<any>;
  text: string;
  onClick(event: MouseEvent<HTMLButtonElement>): void;
  dataTestId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltip?: string;
  theme?: RedoButtonTheme;
  size?: RedoButtonSize;
  show?: boolean;
  id?: string;
}

export const RedoCommandMenu = memo(function RedoCommandMenu({
  dontRestrainIconWidth,
  items,
  open,
  setOpen,
  anchor,
  size = "sm",
  fitToAnchor = false,
  placement,
  dropdownClassName,
}: {
  dontRestrainIconWidth?: boolean;
  items: RedoCommandMenuItem[];
  open: boolean;
  setOpen(value: boolean): void;
  anchor: HTMLElement | null;
  size?: RedoButtonSize;
  fitToAnchor?: boolean;
  placement?: PopperPlacementType;
  dropdownClassName?: string;
}) {
  const atLeastOneIcon = items.some((item) => item.Icon !== undefined);

  const itemsToShow = useMemo(
    () => items.filter((item) => item.show !== false),
    [items],
  );

  const dropdown = (
    <Dropdown
      anchor={anchor}
      className={dropdownClassName}
      fitToAnchor={fitToAnchor}
      open={open}
      placement={placement}
    >
      <Flex dir="column">
        {itemsToShow.map((item, idx) => {
          return (
            <RedoCommandMenuItem
              dontRestrainIconWidth={dontRestrainIconWidth}
              fitToAnchor={fitToAnchor}
              indentForIcon={atLeastOneIcon}
              item={item}
              itemClicked={() => setOpen(false)}
              key={idx}
              size={size}
            />
          );
        })}
      </Flex>
    </Dropdown>
  );

  return (
    <>
      {open && (
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
          }}
        >
          {dropdown}
        </ClickAwayListener>
      )}
    </>
  );
});

/**
 * @param indentForIcon if at least one of the menu items has an icon,
 * we want to space all the buttons evenly
 */
const RedoCommandMenuItem = memo(function RedoCommandMenuItem({
  dontRestrainIconWidth,
  item,
  indentForIcon,
  itemClicked,
  size = "sm",
  fitToAnchor = false,
}: {
  dontRestrainIconWidth?: boolean;
  item: RedoCommandMenuItem;
  indentForIcon: boolean;
  itemClicked(): void;
  size?: RedoButtonSize;
  fitToAnchor?: boolean;
}) {
  const icon = item.Icon || (indentForIcon ? "placeholder" : undefined);

  const button = (
    <RedoButton
      buttonId={item.id}
      centerItems={false}
      className={
        fitToAnchor
          ? redoCommandMenuCss.fullWidthDropdownButton
          : redoCommandMenuCss.dropdownButton
      }
      dataTestId={item.dataTestId}
      disabled={item.disabled}
      dontRestrainIconWidth={dontRestrainIconWidth}
      hierarchy="tertiary"
      IconLeading={icon}
      onClick={(event) => {
        event.stopPropagation();
        itemClicked();
        item.onClick(event);
      }}
      size={item.size ?? size}
      text={item.text}
      theme={item.theme}
      useOverflowTooltip
    />
  );
  const tooltipText = (item.disabled && item.disabledTooltip) || item.tooltip;

  if (tooltipText) {
    return (
      <Tooltip arrow title={tooltipText}>
        <div>{button}</div>
      </Tooltip>
    );
  }

  return button;
});
